import { filter, map, tap } from 'rxjs/operators';
import { merge, Observable, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { AccountInfo } from '@azure/msal-browser';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { inject, Injectable } from '@angular/core';

import { AuthService } from '../auth.types';

import { authActions, Account } from '@iss/wo-common/ngrx';
import { AuthContext } from '@iss/libs/auth';

@Injectable()
export class MsalAuthService implements AuthService {
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly msalService = inject(MsalService);
  private readonly store = inject(Store);

  public bootstrap(): Observable<unknown> {
    const { msalSubject$ } = this.msalBroadcastService;
    const redirect$ = this.msalService.handleRedirectObservable();

    const authenticated$ = this.msalBroadcastService
      .inProgress$
      .pipe(
        map(() => this.findActiveAccount()),
        filter(Boolean),
        tap(account => this.store.dispatch(authActions.authenticated({ account: this.toAccount(account) }))),
      );

    return merge(redirect$, msalSubject$, authenticated$);
  }

  private toAccount(account: AccountInfo): Account {
    return {
      email: AuthContext.inferEmail(account.idTokenClaims?.['email'] as string | undefined),
      username: AuthContext.inferUsername(
        account.idTokenClaims?.['family_name'] as string | undefined,
        account.idTokenClaims?.['given_name'] as string | undefined,
        account.username,
      ),
    };
  }

  public logout(): Observable<unknown> {
    return this.msalService.logoutRedirect({
      account: this.msalService.instance.getActiveAccount(),
    });
  }

  public isLoggedIn(): Observable<boolean> {
    return of(Boolean(this.findActiveAccount()));
  }

  private findActiveAccount(): AccountInfo | null {
    const { instance } = this.msalService;
    const activeAccount = instance.getActiveAccount();
    const accounts = instance.getAllAccounts();
    if (activeAccount != null) return activeAccount;
    if (accounts.length > 0) {
      const [ firstAccount ] = accounts;
      instance.setActiveAccount(firstAccount);
      return firstAccount;
    }
    return null;
  }
}
